import { useMutation } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useInvalidateServiceItems } from './serviceQueries';
import type {
    ServiceItem,
    ServiceItemPriority,
    ServiceItemState,
    ServiceItemStatus,
} from './serviceTypes';

export interface ServiceItemInput {
    owner?: string | null;
    email_date?: string | null;
    community_id?: string | null;
    community_raw?: string | null;
    lot?: string | null;
    address?: string | null;
    contact?: string | null;
    builder_contact?: string | null;
    is_warranty?: boolean;
    schedule_state?: ServiceItemState;
    scheduled_date?: string | null;
    schedule_note?: string | null;
    priority?: ServiceItemPriority;
    type_of_work?: string;
    requires_concrete?: boolean;
    extended_time?: boolean;
    notes?: string | null;
    crew_id?: string | null;
    backcharge_crew_id?: string | null;
    status?: ServiceItemStatus;
    completion_date?: string | null;
    completed_by?: string | null;
    billable?: boolean;
    epo_amount?: number | null;
    epo_note?: string | null;
}

/**
 * Keeps schedule_state and scheduled_date honest with each other.
 *
 * The DB check constraint says a 'scheduled' item must carry a date. Three
 * distinct cases, and getting any of them wrong surfaces a raw Postgres error
 * to the person editing a cell:
 *
 *   date set        -> the row is scheduled, whatever the dropdown said
 *   date cleared    -> a row that was scheduled can no longer be
 *   state set to
 *   'scheduled'     -> only legal if the row ALREADY has a date. The caller
 *                      should prevent this, but if it slips through we leave
 *                      the previous state rather than writing an illegal row.
 *
 * `previous` matters here. Reading the state only from the patch means an edit
 * that touches one field cannot tell whether the OTHER field already has a
 * value, and it will happily write a combination the database rejects.
 */
function reconcileSchedule(
    input: ServiceItemInput,
    previous?: ServiceItem,
): ServiceItemInput {
    const out = { ...input };
    const touchesDate = 'scheduled_date' in out;
    const touchesState = 'schedule_state' in out;

    if (touchesDate && out.scheduled_date) {
        out.schedule_state = 'scheduled';
        return out;
    }

    if (touchesDate && out.scheduled_date === null) {
        const nextState = touchesState ? out.schedule_state : previous?.schedule_state;
        if (nextState === 'scheduled') out.schedule_state = 'unscheduled';
        return out;
    }

    if (touchesState && out.schedule_state === 'scheduled') {
        if (!previous?.scheduled_date) {
            out.schedule_state = previous?.schedule_state ?? 'unscheduled';
        }
        return out;
    }

    if (touchesState && out.schedule_state && out.schedule_state !== 'scheduled') {
        // Moving off scheduled drops the date so the two cannot disagree.
        out.scheduled_date = null;
    }

    return out;
}

/**
 * Keeps completion fields honest with status.
 *
 * Only runs when status is ACTUALLY changing. The earlier version fired on
 * every write, so editing a lot number on an open row also nulled
 * completion_date and completed_by. Harmless while those fields are empty, and
 * silent data loss the first time somebody records who handled a job.
 *
 * completion_date stays nullable even on completed items, because the source
 * spreadsheet does not reliably record one and inventing a date would put a
 * false value into a record used for billing. So this fills a date when
 * somebody completes an item now, and clears the fields when an item is
 * reopened, but never fabricates one for historical rows.
 */
function reconcileCompletion(
    input: ServiceItemInput,
    previous?: ServiceItem,
): ServiceItemInput {
    const out = { ...input };
    if (!('status' in out) || out.status === undefined) return out;
    if (previous && out.status === previous.status) return out;

    if (out.status !== 'open') {
        if (out.completion_date === undefined && !previous?.completion_date) {
            out.completion_date = new Date().toISOString().slice(0, 10);
        }
    } else {
        out.completion_date = null;
        out.completed_by = null;
    }
    return out;
}

export function useCreateServiceItem() {
    const invalidate = useInvalidateServiceItems();
    return useMutation({
        mutationFn: async (input: ServiceItemInput): Promise<ServiceItem> => {
            const {
                data: { user },
            } = await supabase.auth.getUser();

            const payload = reconcileCompletion(reconcileSchedule(input, undefined), undefined);
            const { data, error } = await supabase
                .from('service_items')
                .insert({
                    ...payload,
                    type_of_work: payload.type_of_work?.trim() || '(not recorded)',
                    created_by: user?.id ?? null,
                    updated_by: user?.id ?? null,
                })
                .select('*')
                .single();
            if (error) throw new Error(error.message);
            return data as ServiceItem;
        },
        onSuccess: () => invalidate(),
    });
}

export function useUpdateServiceItem() {
    const invalidate = useInvalidateServiceItems();
    return useMutation({
        mutationFn: async ({
            id,
            input,
            previous,
        }: {
            id: string;
            input: ServiceItemInput;
            previous?: ServiceItem;
        }): Promise<ServiceItem> => {
            const {
                data: { user },
            } = await supabase.auth.getUser();

            const payload = reconcileCompletion(reconcileSchedule(input, previous), previous);
            const { data, error } = await supabase
                .from('service_items')
                .update({ ...payload, updated_by: user?.id ?? null })
                .eq('id', id)
                .select('*')
                .single();
            if (error) throw new Error(error.message);
            return data as ServiceItem;
        },
        onSuccess: (_d, vars) => invalidate(vars.id),
    });
}

export function useDeleteServiceItem() {
    const invalidate = useInvalidateServiceItems();
    return useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const { error } = await supabase.from('service_items').delete().eq('id', id);
            if (error) throw new Error(error.message);
        },
        onSuccess: () => invalidate(),
    });
}

/**
 * Bulk assign a crew and a date across a selection.
 *
 * This is how a day gets built: filter to a section, select the items, assign a
 * tech and a date. Deliberately does not touch status, because scheduling work
 * and completing it are different actions and merging them is how a board ends
 * up with items marked done that nobody did.
 *
 * Returns the number of rows actually changed so the dialog can report the real
 * result rather than assuming the whole selection moved.
 */
export function useBulkScheduleServiceItems() {
    const invalidate = useInvalidateServiceItems();
    return useMutation({
        mutationFn: async ({
            ids,
            crewId,
            isoDate,
        }: {
            ids: string[];
            crewId: string | null;
            isoDate: string | null;
        }): Promise<number> => {
            if (!ids.length) return 0;
            const {
                data: { user },
            } = await supabase.auth.getUser();

            const patch: Record<string, unknown> = { updated_by: user?.id ?? null };
            if (crewId !== undefined) patch.crew_id = crewId;
            if (isoDate) {
                patch.scheduled_date = isoDate;
                patch.schedule_state = 'scheduled';
            }

            const { data, error } = await supabase
                .from('service_items')
                .update(patch)
                .in('id', ids)
                .select('id');
            if (error) throw new Error(error.message);
            return (data ?? []).length;
        },
        onSuccess: () => invalidate(),
    });
}

/**
 * Delete items outright.
 *
 * Kristine, 9 Sep: "we do not have a delete button. Can you please add for us."
 *
 * A real delete and not an archive. Completed already exists for work that
 * finished, so the only reason left to remove a row is that it should never
 * have been there: a duplicate, a mis-keyed lot, something entered twice.
 * Archiving those would leave the office scrolling past rows they have already
 * decided are wrong.
 *
 * One statement rather than a loop over useDeleteServiceItem, so twenty rows is
 * one round trip and either all of them go or none do. Returns the number
 * actually removed so the bar reports the real result instead of assuming the
 * whole selection went.
 */
export function useBulkDeleteServiceItems() {
    const invalidate = useInvalidateServiceItems();
    return useMutation({
        mutationFn: async (ids: string[]): Promise<number> => {
            if (!ids.length) return 0;
            const { data, error } = await supabase
                .from('service_items')
                .delete()
                .in('id', ids)
                .select('id');
            if (error) throw new Error(error.message);
            return (data ?? []).length;
        },
        onSuccess: () => invalidate(),
    });
}

/**
 * Mark items complete in bulk.
 *
 * Billable items go to 'completed_needs_billing' so they land in front of
 * whoever prices them. Non-billable go to 'completed'.
 *
 * That second route changed on 25 Aug 2026. It used to send them to
 * 'completed_billed', which was the only finished state available at the time
 * and which claims an invoice went out. For a warranty callback or anything
 * else PPC was never going to charge for, that is a false record sitting in a
 * table the office reads for billing. Kristine spotted it from the wording.
 *
 * Splitting the two rather than running one blanket update is still what stops
 * a billable item from being quietly closed out without ever being invoiced.
 */
export function useBulkCompleteServiceItems() {
    const invalidate = useInvalidateServiceItems();
    return useMutation({
        mutationFn: async ({
            billableIds,
            nonBillableIds,
            isoDate,
            completedBy,
        }: {
            billableIds: string[];
            nonBillableIds: string[];
            isoDate: string;
            completedBy?: string | null;
        }): Promise<{ needsBilling: number; completed: number }> => {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            const base = {
                completion_date: isoDate,
                completed_by: completedBy ?? null,
                updated_by: user?.id ?? null,
            };

            let needsBilling = 0;
            let completed = 0;

            if (billableIds.length) {
                const { data, error } = await supabase
                    .from('service_items')
                    .update({ ...base, status: 'completed_needs_billing' })
                    .in('id', billableIds)
                    .select('id');
                if (error) throw new Error(error.message);
                needsBilling = (data ?? []).length;
            }

            if (nonBillableIds.length) {
                const { data, error } = await supabase
                    .from('service_items')
                    .update({ ...base, status: 'completed' })
                    .in('id', nonBillableIds)
                    .select('id');
                if (error) throw new Error(error.message);
                completed = (data ?? []).length;
            }

            return { needsBilling, completed };
        },
        onSuccess: () => invalidate(),
    });
}
